/**
 * Per-client proposals list + "New Proposal" button.
 *
 * The list shows status, totals snapshot, valid_until and quick actions.
 * Clicking a row opens the full editor at /clients/:id/proposals/:proposal_id.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, FileText, ExternalLink, Trash2, Loader2, Mail,
  CalendarClock, CheckCircle2, XCircle, Archive, Eye, Files,
} from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
  internal_review: "bg-amber-100 text-amber-800 border-amber-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  viewed: "bg-indigo-100 text-indigo-800 border-indigo-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  declined: "bg-red-100 text-red-800 border-red-200",
  expired: "bg-orange-100 text-orange-800 border-orange-200",
  archived: "bg-zinc-200 text-zinc-600 border-zinc-300",
};

function fmtMoney(v, cur = "AUD") {
  const sym = { AUD: "$", USD: "$", GBP: "£", EUR: "€", NZD: "$" }[cur] || "";
  return `${sym}${Math.round(Number(v || 0)).toLocaleString()}`;
}

export default function ClientProposalsPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/proposals`);
      setRows(data?.proposals || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load proposals");
      setRows([]);
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const openPicker = async () => {
    setPickerOpen(true);
    if (templates === null) {
      try {
        const { data } = await api.get(`/proposal-templates`);
        setTemplates(data?.templates || []);
      } catch (e) {
        setTemplates([]);
      }
    }
  };

  const createBlank = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/proposals`, {});
      toast.success("Proposal created");
      setPickerOpen(false);
      navigate(`/clients/${clientId}/proposals/${data.proposal_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const createFromTemplate = async (templateId) => {
    setCreating(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/proposals/from-template/${templateId}`,
      );
      toast.success("Proposal created from template");
      setPickerOpen(false);
      navigate(`/clients/${clientId}/proposals/${data.proposal_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this proposal? This cannot be undone.")) return;
    try {
      await api.delete(`/proposals/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="client-proposals-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Sales</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Proposals</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            Build, review and send fixed-scope sales proposals — website builds,
            ongoing digital marketing programs, content packages and paid-ads
            management. The PDF + share link are delivered automatically over
            SendGrid when you press Send.
          </p>
        </div>
        <Button onClick={openPicker} disabled={creating}
          className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
          data-testid="new-proposal-btn">
          {creating
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Plus className="h-4 w-4 mr-2" />}
          New Proposal
        </Button>
      </div>

      {rows === null ? (
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </CardContent></Card>
      ) : rows.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="p-10 text-center space-y-3">
            <FileText className="h-10 w-10 text-zinc-300 mx-auto" />
            <div className="font-display font-bold text-xl">No proposals yet</div>
            <p className="text-sm text-zinc-500 max-w-lg mx-auto">
              Click <strong>New Proposal</strong> to build a sales document.
              You can customise every line item, attach domain options, set an
              expiry date and email the PDF + a shareable web link to the
              client when you&apos;re ready.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="proposals-list">
          {rows.map((p) => (
            <Card key={p.proposal_id} className="rounded-sm hover:border-zinc-400 transition-colors">
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-12 md:col-span-8 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/clients/${clientId}/proposals/${p.proposal_id}`}
                        className="font-display font-bold text-base hover:underline truncate"
                        data-testid={`proposal-title-${p.proposal_id}`}>
                        {p.title || "Untitled proposal"}
                      </Link>
                      <Badge className={`rounded-sm font-mono text-[10px] ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}
                        data-testid={`proposal-status-${p.proposal_id}`}>
                        {p.status}
                      </Badge>
                    </div>
                    <div className="text-[11px] font-mono text-zinc-500 mt-0.5 truncate"
                      data-testid={`proposal-meta-${p.proposal_id}`}>
                      For {p.prepared_for || "—"} · Created{" "}
                      {p.created_at
                        ? new Date(p.created_at).toLocaleString("en-AU", {
                            day: "2-digit", month: "short", year: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          })
                        : "—"}
                      {p.valid_until && <> · <CalendarClock className="inline h-3 w-3" /> valid until {p.valid_until.slice(0, 10)}</>}
                    </div>
                  </div>

                  <div className="col-span-12 md:col-span-4 flex items-center justify-end gap-1.5 flex-wrap">
                    {p.status === "accepted" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    {p.status === "declined" && <XCircle className="h-4 w-4 text-red-600" />}
                    {p.status === "archived" && <Archive className="h-4 w-4 text-zinc-400" />}
                    {p.share_token && (
                      <a target="_blank" rel="noreferrer"
                        href={`/proposals/share/${p.share_token}`}
                        className="text-xs underline text-zinc-600"
                        data-testid={`proposal-share-link-${p.proposal_id}`}>
                        <ExternalLink className="h-3.5 w-3.5 inline" /> Share
                      </a>
                    )}
                    {p.sent_at && (
                      <span className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" /> sent
                      </span>
                    )}
                    <Button size="sm" variant="outline" className="rounded-sm h-7"
                      onClick={() => window.open(`/proposals/preview/${p.proposal_id}`, "_blank")}
                      data-testid={`proposal-view-${p.proposal_id}`}
                      title="View as the client will see it">
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7"
                      onClick={() => navigate(`/clients/${clientId}/proposals/${p.proposal_id}`)}
                      data-testid={`proposal-edit-${p.proposal_id}`}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600"
                      onClick={() => remove(p.proposal_id)}
                      data-testid={`proposal-delete-${p.proposal_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Full Investment summary — matches URL + PDF (ex-GST
                    headline, inc-GST in fine print). Reads the totals
                    snapshot returned by the list endpoint, which is
                    always recomputed server-side via compute_totals so
                    list / URL / PDF can never drift. */}
                <ListTotalsStrip totals={p.totals || {}} cur={p.currency || "AUD"}
                  proposalId={p.proposal_id} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Picker — "From scratch" vs "Use a template" */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid="new-proposal-picker">
          <DialogHeader>
            <DialogTitle>Create a new proposal</DialogTitle>
            <DialogDescription>
              Start from a blank proposal, or pick a template to clone its
              sections and pricing into a fresh draft.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button type="button" onClick={createBlank} disabled={creating}
              className="w-full text-left border border-zinc-200 hover:border-zinc-900 rounded-sm p-4 transition-colors disabled:opacity-50"
              data-testid="picker-from-scratch">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 grid place-items-center bg-zinc-100 rounded-sm">
                  <Plus className="h-5 w-5 text-zinc-700" />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-base">Start from scratch</div>
                  <div className="text-xs text-zinc-500">
                    Uses the default catalog (website build, hosting,
                    marketing programs, content, ads management).
                  </div>
                </div>
              </div>
            </button>

            <div>
              <div className="label-overline text-[10px] mt-2 mb-1.5">From a template</div>
              {templates === null ? (
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading templates…
                </div>
              ) : templates.length === 0 ? (
                <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-sm p-3"
                  data-testid="picker-no-templates">
                  No templates yet. Build one from the{" "}
                  <Link to="/templates" className="underline text-zinc-700"
                    onClick={() => setPickerOpen(false)}>
                    Proposal Templates
                  </Link>{" "}
                  menu, or open an existing proposal and click
                  <strong> Copy to templates</strong>.
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1"
                  data-testid="picker-templates-list">
                  {templates.map((t) => (
                    <button key={t.template_id} type="button"
                      onClick={() => createFromTemplate(t.template_id)}
                      disabled={creating}
                      className="w-full text-left border border-zinc-200 hover:border-zinc-900 rounded-sm p-3 transition-colors disabled:opacity-50 flex items-center gap-3"
                      data-testid={`picker-template-${t.template_id}`}>
                      <div className="h-9 w-9 grid place-items-center bg-purple-50 rounded-sm">
                        <Files className="h-4 w-4 text-purple-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{t.title}</div>
                        <div className="text-[11px] font-mono text-zinc-500 truncate">
                          {(t.sections || []).length} sections
                          {t.totals?.one_off_total
                            ? ` · One-off ${fmtMoney(t.totals.one_off_total, t.currency)}`
                            : ""}
                          {t.totals?.monthly_total
                            ? ` · Monthly ${fmtMoney(t.totals.monthly_total, t.currency)}`
                            : ""}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)}
              className="rounded-sm">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Dark, compact 6-column totals strip rendered under each proposal row.
 * Mirrors the layout/order used on the URL share view + PDF investment
 * summary: One-off · Monthly · Quarterly · Annual · Biennial · First-year.
 * Big number = ex-GST; fine print = `inc GST $X.XX`.
 */
function ListTotalsStrip({ totals, cur, proposalId }) {
  const cells = [
    { label: "One-off",   ex: totals.one_off_subtotal,   inc: totals.one_off_total },
    { label: "Monthly",   ex: totals.monthly_subtotal,   inc: totals.monthly_total },
    { label: "Quarterly", ex: totals.quarterly_subtotal, inc: totals.quarterly_total },
    { label: "Annual",    ex: totals.annual_subtotal,    inc: totals.annual_total },
    { label: "Biennial",  ex: totals.biennial_subtotal,  inc: totals.biennial_total },
    { label: "First-year total", ex: totals.year_one_subtotal, inc: totals.year_one_total, highlight: true },
  ];
  return (
    <div className="bg-[#0B0B0B] text-white rounded-sm px-4 py-3 grid grid-cols-3 md:grid-cols-6 gap-3"
      data-testid={`proposal-totals-${proposalId}`}>
      {cells.map((c) => (
        <div key={c.label} className={c.highlight ? "border-l border-zinc-700 pl-3" : ""}>
          <div className="text-[9px] uppercase tracking-widest text-zinc-400 truncate">{c.label}</div>
          <div className={`font-display font-extrabold ${c.highlight ? "text-base" : "text-sm"} mt-0.5`}>
            {fmtMoney(c.ex, cur)}
          </div>
          <div className="text-[9px] font-mono text-zinc-500 mt-0.5">
            inc GST {fmtMoney(c.inc, cur)}
          </div>
        </div>
      ))}
    </div>
  );
}
